import { useEffect, useMemo, useState } from "react";
import type { ApiError } from "@/lib/api";
import { errorMessage } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import type { MatchSummary } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { useMatches } from "@/features/matches/useMatches";
import { CoachModal } from "./CoachModal";
import { Button } from "@/components/ui/Button";
import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionPagination,
  CollectionToolbar,
} from "@/features/collections/CollectionControls";
import {
  coerceVisibility,
  visibilitySegmentOptions,
} from "../visibilityVocabulary";
import { useTeamSettings } from "../hooks/useTeamSettings";
import { useCoachNotesList } from "../hooks/useCoachNotesList";
import { useCoachClipsList } from "../hooks/useCoachClipsList";
import type { MatchSummaryMutations } from "../hooks/useCoachSummariesList";
import { TkticSelect } from "@/components/ui/TkticSelect";

/**
 * Match-summary composer modal — Coach > Match Summaries.
 *
 * Ports the legacy `openCoachMatchSummaryModal`
 * (`js/coaching/match-summaries.js`). `null` `summary` ⇒ create;
 * a `MatchSummary` ⇒ edit.
 *
 * Fields mirror `CreateMatchSummaryRequest`:
 *   - match (LOCKED on edit — a summary's match is immutable;
 *     legacy disabled the select with a hint)
 *   - visibility
 *   - team_positives / team_improvements / training_focus / body
 *     — the recap text fields
 *   - note_ids / clip_ids — linked evidence,
 *     picked from checklists. Privacy: the BACKEND filters
 *     private linked sources out of viewer payloads — the
 *     composer just lets a coach attach them; it never exposes
 *     them itself.
 *   - playlist_ids — legacy evidence from the retired Sessions
 *     surface. Existing rows may still carry it from the API. The
 *     current composer shows the count and explicitly clears it on
 *     save so old invisible links do not survive silently.
 *
 * Like the playlist composer, this is its own modal — there's no
 * Coach Review surface for match summaries.
 */

// One vocabulary for every composer — see visibilityVocabulary.ts.
//
// `player` is omitted here: match summaries have no `player_ids`
// column, and `filter_match_summaries_for_user` only ever surfaces
// `team` summaries to viewers, so a `player`-visibility
// summary would be invisible to the family it's meant for. `private`
// stays — it's a legit coach-only state (a recap that intentionally
// doesn't reach viewers), same as a draft.
const VISIBILITY_OPTIONS = visibilitySegmentOptions(["player"]);

export type MatchSummaryComposerModalProps = {
  summary: MatchSummary | null;
  mutations: MatchSummaryMutations;
  onClose: () => void;
  onSaved?: () => void;
  initialMatchId?: string;
};

type RecapEvidenceItem = {
  key: string;
  kind: "note" | "clip";
  id: number;
  title: string;
  metadata: string;
  visibility: string;
};

export function MatchSummaryComposerModal({
  summary,
  mutations,
  onClose,
  onSaved,
  initialMatchId,
}: MatchSummaryComposerModalProps) {
  const isEdit = summary !== null;
  const toast = useToast();
  const matchesQuery = useMatches();
  const notesQuery = useCoachNotesList();
  const clipsQuery = useCoachClipsList();

  const matches = matchesQuery.data ?? [];
  const [matchId, setMatchId] = useState(
    summary?.match_id ?? initialMatchId ?? matches[0]?.id ?? "",
  );
  // The team's own "Match recap visibility" setting decides where a
  // NEW recap starts. It was stored and served but never read, so
  // setting it changed nothing; honouring it here is what makes the
  // control real. With no setting, new recaps default to `team`: a
  // recap is a write-up FOR the squad, and defaulting the one
  // squad-wide composer to invisible meant a coach could write the
  // whole thing, save, and reach nobody. Editing keeps the recap's
  // own visibility.
  const teamSettingsQuery = useTeamSettings();
  const defaultSummaryVisibility =
    teamSettingsQuery.data?.settings?.["summaries.default_visibility"];
  const [visibility, setVisibility] = useState<string>(() => {
    // A summary persisted before a value was dropped would have no
    // matching <option>, so the select would render blank. Two such
    // values exist: `player` (never deliverable for a recap — see the
    // options comment above) and `unlisted` (retired in 0054, and
    // always identical to `team` in every read path). Both collapse to
    // `team`, the closest viewer-facing intent.
    if (!summary) return "team";
    const coerced = coerceVisibility(summary.visibility);
    return coerced === "player" ? "team" : coerced;
  });
  // `useTeamSettings` is an async query with no `initialData`, and this
  // modal mounts on a click — so at mount the setting is almost always
  // still undefined. Reading it in the initializer above would silently
  // fall through to the `team` fallback and never correct itself, which
  // fails toward WIDER sharing: a team that configured recaps as
  // coach-only would get a squad-visible one. Seed on arrival instead.
  //
  // Guarded so it only ever seeds a starting point: never on edit (the
  // recap's own visibility wins), and never after the coach has touched
  // the control.
  const [visibilityTouched, setVisibilityTouched] = useState(false);
  useEffect(() => {
    if (isEdit || visibilityTouched || !defaultSummaryVisibility) return;
    setVisibility(
      defaultSummaryVisibility === "player" ? "team" : defaultSummaryVisibility,
    );
  }, [defaultSummaryVisibility, isEdit, visibilityTouched]);
  const [teamPositives, setTeamPositives] = useState(
    summary?.team_positives ?? "",
  );
  const [teamImprovements, setTeamImprovements] = useState(
    summary?.team_improvements ?? "",
  );
  const [trainingFocus, setTrainingFocus] = useState(
    summary?.training_focus ?? "",
  );
  const [body, setBody] = useState(summary?.body ?? "");
  const [noteIds, setNoteIds] = useState<Set<number>>(
    () => new Set(summary?.note_ids ?? []),
  );
  const [clipIds, setClipIds] = useState<Set<number>>(
    () => new Set(summary?.clip_ids ?? []),
  );
  const [evidenceVisibility, setEvidenceVisibility] = useState("");
  const legacyPlaylistCount = summary?.playlist_ids?.length ?? 0;
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // If matches load after mount, default the select to the first.
  const resolvedMatchId =
    matchId || (isEdit ? (summary?.match_id ?? "") : matches[0]?.id ?? "");

  const matchLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of matches) {
      const teams = `${m.home_team} vs ${m.away_team}`;
      map.set(m.id, m.date ? `${teams} · ${m.date}` : teams);
    }
    return map;
  }, [matches]);

  const toggle = <V extends number>(
    setter: React.Dispatch<React.SetStateAction<Set<V>>>,
  ) => (id: V) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolvedMatchId) {
      setError("Pick a match for this recap.");
      return;
    }
    // The backend rejects an all-blank summary (the merged-state
    // validator); block it inline so the coach gets a clear
    // message instead of a 422.
    const hasText = [teamPositives, teamImprovements, trainingFocus, body].some(
      (t) => t.trim(),
    );
    if (!hasText) {
      setError("Add at least one recap section before saving.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      if (isEdit) {
        // The match is immutable — the PATCH body omits `match_id`.
        await mutations.updateSummary(summary.id, {
          visibility,
          team_positives: teamPositives.trim(),
          team_improvements: teamImprovements.trim(),
          training_focus: trainingFocus.trim(),
          body: body.trim(),
          note_ids: [...noteIds],
          clip_ids: [...clipIds],
          playlist_ids: [],
        });
      } else {
        await mutations.createSummary({
          match_id: resolvedMatchId,
          visibility,
          team_positives: teamPositives.trim(),
          team_improvements: teamImprovements.trim(),
          training_focus: trainingFocus.trim(),
          body: body.trim(),
          note_ids: [...noteIds],
          clip_ids: [...clipIds],
          playlist_ids: [],
        });
      }
      toast.success(isEdit ? "Match recap updated" : "Match recap created");
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : `Could not ${isEdit ? "update" : "create"} the recap.`,
      );
      toast.error(`Couldn't ${isEdit ? "update" : "create"} match recap`, {
        description: errorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const notes = notesQuery.data?.notes ?? [];
  const clips = clipsQuery.data?.clips ?? [];
  const evidence = useMemo<RecapEvidenceItem[]>(
    () => [
      ...notes.map((note) => ({
        key: `note-${note.id}`,
        kind: "note" as const,
        id: note.id,
        title:
          note.title?.trim() || note.event_title?.trim() || "(untitled note)",
        metadata: `${note.category} · ${note.visibility}`,
        visibility: note.visibility,
      })),
      ...clips.map((clip) => ({
        key: `clip-${clip.id}`,
        kind: "clip" as const,
        id: clip.id,
        title: clip.title?.trim() || "(untitled clip)",
        metadata: `${formatPlayhead(clip.start_seconds)}–${formatPlayhead(clip.end_seconds)} · ${clip.category} · ${clip.visibility}`,
        visibility: clip.visibility,
      })),
    ],
    [clips, notes],
  );
  const evidenceCollection = useCollection(evidence, {
    scope: `match-summary-evidence:${summary?.id ?? "new"}`,
    text: (item) => `${item.title} ${item.metadata}`,
    matches: (item, kind) => item.kind === kind,
    predicate: (item) =>
      !evidenceVisibility || item.visibility === evidenceVisibility,
  });

  return (
    <CoachModal
      title={isEdit ? "Edit match recap" : "New match recap"}
      size="wide"
      onClose={onClose}
      data-testid="summary-composer-modal"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button
            type="submit"
            form="match-summary-composer-form"
            disabled={submitting}
            data-testid="summary-composer-submit"
          >
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Create recap"}
          </Button>
        </div>
      }
    >
      <form
        id="match-summary-composer-form"
        className="flex flex-col gap-3"
        onSubmit={handleSubmit}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Match">
            <TkticSelect
              value={resolvedMatchId}
              onChange={(next) => setMatchId(next)}
              disabled={isEdit || matchesQuery.isLoading}
              data-testid="summary-composer-match"
              ariaLabel="Match"
              options={
                matches.length === 0
                  ? [{ value: "", label: "No matches yet" }]
                  : matches.map((m) => ({
                      value: m.id,
                      label: matchLabelById.get(m.id) ?? m.id,
                    }))
              }
            />
          </Field>
          <Field label="Visibility">
            <TkticSelect
              value={visibility}
              onChange={(next) => {
                setVisibilityTouched(true);
                setVisibility(next);
              }}
              data-testid="summary-composer-visibility"
              ariaLabel="Visibility"
              options={VISIBILITY_OPTIONS.map((v) => ({
                value: v.value,
                label: v.label,
              }))}
            />
          </Field>
        </div>
        {isEdit ? (
          <p className="text-ink-muted text-xs -mt-1">
            Match can't be changed — create a new recap for a
            different match.
          </p>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
          <section className="flex min-w-0 flex-col gap-3">
            <Field label="Team positives">
              <textarea
                value={teamPositives}
                onChange={(e) => setTeamPositives(e.target.value)}
                data-testid="summary-composer-positives"
                rows={2}
                placeholder="What went well as a team?"
                className="w-full bg-canvas border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </Field>
            <Field label="Areas to improve">
              <textarea
                value={teamImprovements}
                onChange={(e) => setTeamImprovements(e.target.value)}
                data-testid="summary-composer-improvements"
                rows={2}
                placeholder="What should we clean up next match?"
                className="w-full bg-canvas border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </Field>
            <Field label="Training focus">
              <textarea
                value={trainingFocus}
                onChange={(e) => setTrainingFocus(e.target.value)}
                data-testid="summary-composer-training"
                rows={2}
                placeholder="Next practice focus"
                className="w-full bg-canvas border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </Field>
            <Field label="Coach recap" helper="Optional full recap for the team.">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                data-testid="summary-composer-body"
                rows={3}
                className="w-full bg-canvas border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </Field>
          </section>

          <aside className="flex min-w-0 flex-col gap-3">
            <div>
              <h3 className="font-display text-base text-ink">Supporting evidence</h3>
              <p className="text-xs text-ink-muted">
                Link relevant notes and clips. Check their visibility before sharing.
              </p>
              <div className="sr-only">
                <span data-testid="summary-composer-notes">
                  {noteIds.size} notes selected
                </span>
                <span data-testid="summary-composer-clips">
                  {clipIds.size} clips selected
                </span>
              </div>
            </div>
            <CollectionToolbar
              collection={evidenceCollection}
              label="notes and clips"
              searchPlaceholder="Title, player or category"
              filterLabel="Kind"
              className="recap-evidence-toolbar"
              filters={[
                { value: "", label: "All kinds" },
                { value: "note", label: "Notes" },
                { value: "clip", label: "Clips" },
              ]}
              extraFiltersAfter
              extraFilters={[
                {
                  label: "Visibility",
                  ariaLabel: "Filter evidence by visibility",
                  value: evidenceVisibility,
                  clearValue: "",
                  options: [
                    { value: "", label: "All visibility" },
                    { value: "private", label: "Coaches only" },
                    { value: "team", label: "Whole team" },
                    { value: "player", label: "Player + family" },
                  ],
                  onChange: setEvidenceVisibility,
                },
              ]}
            />
            <fieldset
              className="flex flex-col gap-1"
              data-testid="summary-composer-evidence"
            >
              <legend className="mb-2 text-sm font-display text-ink">
                Link notes and clips
              </legend>
              {evidenceCollection.items.map((item) => {
                const checked =
                  item.kind === "note"
                    ? noteIds.has(item.id)
                    : clipIds.has(item.id);
                return (
                  <label
                    key={item.key}
                    className="flex cursor-pointer items-center gap-2 rounded px-0 py-1.5 text-sm hover:bg-field"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        item.kind === "note"
                          ? toggle(setNoteIds)(item.id)
                          : toggle(setClipIds)(item.id)
                      }
                      className="peer sr-only"
                    />
                    <span
                      aria-hidden="true"
                      className="mt-0.5 grid size-5 shrink-0 place-items-center rounded border border-rule bg-canvas text-xs text-accent peer-focus-visible:ring-2 peer-focus-visible:ring-accent"
                    >
                      {checked ? "✓" : ""}
                    </span>
                    <span className="min-w-0 truncate text-ink">
                      {item.title}
                    </span>
                  </label>
                );
              })}
            </fieldset>
            <CollectionPagination collection={evidenceCollection} label="notes and clips" />
          </aside>
        </div>
        {legacyPlaylistCount > 0 ? (
          <div
            className="rounded border border-rule bg-elevated px-3 py-2 text-xs text-ink-muted"
            data-testid="summary-composer-legacy-sessions"
          >
            This recap still has {legacyPlaylistCount} legacy review session
            link{legacyPlaylistCount === 1 ? "" : "s"} from before Sessions
            were retired. Saving this recap clears those hidden links.
          </div>
        ) : null}
        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="summary-composer-error"
          >
            {error}
          </p>
        ) : null}

      </form>
    </CoachModal>
  );
}

function Field(props: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      {props.children}
      {props.helper ? (
        <span className="text-xs text-ink-muted">{props.helper}</span>
      ) : null}
    </label>
  );
}
